import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { MessageSquare, DollarSign, Users, TrendingUp } from "lucide-react";
import Navbar from "../../components/dashboard/Navbar";
import RecentMessages from "../../components/dashboard/RecentMessages";
import StatsGrid from "../../components/dashboard/StatsGrid";
import SmsUsageCard from "../../components/dashboard/SmsUsageCard";
import { toast } from "react-toastify";
import Loader from "../../components/utils/Loader";
import dashboardRequests from "../../api/DashboardRequests";

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [statsData, setStatsData] = useState([]);
  const [messagesData, setMessagesData] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      try {
        const response = await dashboardRequests.getDashboardStats();
        if (response.data.status_code === "000") {
          setStatsData(response.data.data);
          setMessagesData(response.data.data.recent_messages);
        } else {
          toast.error(response.data.reason);
        }
      } catch (error) {
        toast.error(error.response?.data?.reason || "An error occurred");
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);
  const admin = useSelector((state) => state.admin.txtconnect_ng_admin);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  const filteredStatsData = [
    {
      title: "Bulk Messages Sent Today",
      value: statsData.total_bulk_messages,
      icon: MessageSquare,
      color: "blue",
      bgColor: "bg-blue-100",
      textColor: "text-blue-600",
    },
    {
      title: "Quick Messages Today",
      value: statsData.total_quick_sms_today,
      icon: TrendingUp,
      color: "red",
      bgColor: "bg-red-100",
      textColor: "text-red-600",
    },
    {
      title: "Total Accounts",
      value: statsData.total_accounts,
      icon: Users,
      color: "yellow",
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-600",
    },
    {
      title: "Total Revenue",
      value: `GH₵${Number(statsData.total_revenue).toFixed(2)}`,
      icon: DollarSign,
      color: "purple",
      bgColor: "bg-purple-100",
      textColor: "text-purple-600",
    },
  ];

  const recentMessages = messagesData.map((message) => ({
    id: message.id,
    senderName: message.sender_name,
    senderId: message.sender_id,
    message: message.content,
    type: message.sms_type === "bulk" ? "Bulk" : "Quick",
    status: message.status,
    time: message.date,
    statusColor:
      message.status === "Delivered"
        ? "bg-red-100 text-red-800"
        : message.status === "Failed"
        ? "bg-red-100 text-red-800"
        : "bg-yellow-100 text-yellow-800",
    typeColor:
      message.sms_type === "bulk"
        ? "bg-blue-100 text-blue-800"
        : "bg-red-100 text-red-800",
  }));

  if (isLoading) {
    return <Loader />;
  }

  return (
    <motion.div
      className="min-h-screen bg-gray-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Navbar />

      <main className="py-6 mx-auto max-w-8xl sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Welcome Section */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="mb-2 text-3xl font-bold text-gray-900">
              Welcome back, {admin?.first_name}!
            </h1>
            <p className="text-gray-600">
              Here's what's happening with your admin dashboard today.
            </p>
          </motion.div>

          {/* Stats Grid — shared wrapper so the SMS Usage card (its own
              independent fetch + filter, not part of the generic statsData
              array) can sit in the same row as the 4 generic cards. */}
          <motion.div
            className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-5"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <StatsGrid statsData={filteredStatsData} cardVariants={cardVariants} />
            <SmsUsageCard cardVariants={cardVariants} />
          </motion.div>
          {/* Recent Messages */}
          <RecentMessages recentMessages={recentMessages} />
        </div>
      </main>
    </motion.div>
  );
};
export default Dashboard;
